import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  IconButton,
  Button,
  Box,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  Drawer,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MergeIcon from '@mui/icons-material/MergeType';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { trackMerge } from '../services/analytics';

const BACKEND_API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function Basket({ items, onRemove, mobileOpen, onMobileClose }) {
  const [mergeOptions, setMergeOptions] = useState({
    ocr: true,
    bookmarks: true,
  });
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState('merged_exam_papers');

  const handleMerge = async () => {
    console.log('=== MERGE BUTTON CLICKED ===');
    console.log('Basket items:', items.length);
    console.log('Items:', items);
    
    if (items.length === 0) {
      setError('No documents to merge');
      return;
    }
    
    setMerging(true);
    setError(null);
    
    try {
      console.log('Starting merge with:', { 
        url: `${BACKEND_API}/merge`,
        itemCount: items.length,
        items: items.map(i => ({ title: i.title, url: i.url }))
      });
      
      const response = await axios.post(`${BACKEND_API}/merge`, {
        documents: items,
        ocr: mergeOptions.ocr,
        bookmarks: mergeOptions.bookmarks
      }, {
        responseType: 'blob',
        timeout: 120000 // 2 minute timeout
      });
      
      console.log('Merge response received:', {
        status: response.status,
        size: response.data.size,
        type: response.data.type
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const downloadFileName = fileName.trim() || 'merged_exam_papers';
      link.setAttribute('download', `${downloadFileName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log('Download initiated successfully');
      
      // Track merge analytics (non-blocking)
      const finalFileName = fileName.trim() || 'merged_exam_papers';
      trackMerge(items.length, finalFileName).catch(err => 
        console.error('Analytics error:', err)
      );
      
      // Show success message
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
    } catch (err) {
      console.error('Merge error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config
      });
      
      let errorMessage = 'Failed to merge PDFs. ';
      if (err.response) {
        errorMessage += `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage += 'No response from server. Check if backend is running.';
      } else {
        errorMessage += err.message;
      }
      
      setError(errorMessage);
    } finally {
      setMerging(false);
    }
  };

  const handleClearAll = () => {
    items.forEach(item => onRemove(item.id));
  };

  const basketContent = (
    <Paper className="glass-dark rounded-2xl p-4" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <ShoppingBasketIcon sx={{ color: '#009688', fontSize: 28 }} />
        <Typography variant="h6" component="div">
          Collection
        </Typography>
        <Chip 
          label={items.length} 
          size="small" 
          sx={{ 
            ml: 'auto',
            bgcolor: '#009688',
            color: 'white',
            fontWeight: 600
          }} 
        />
        {/* Close button for mobile */}
        <IconButton
          onClick={onMobileClose}
          sx={{ 
            display: { xs: 'block', lg: 'none' },
            color: '#009688',
            ml: 0
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {items.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <ShoppingBasketIcon sx={{ fontSize: 60, color: 'rgba(0, 150, 136, 0.3)', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Your collection is empty
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add papers to merge them
          </Typography>
        </Box>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
              PDFs merged successfully! Download started.
            </Alert>
          )}
          
          <List sx={{ maxHeight: 'calc(100vh - 450px)', minHeight: 200, overflow: 'auto', mb: 2, flex: 1 }}>
            {items.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem
                  sx={{ 
                    px: 0,
                    '&:hover': {
                      bgcolor: 'rgba(0, 150, 136, 0.1)',
                      borderRadius: 1,
                    }
                  }}
                  secondaryAction={
                    <IconButton 
                      edge="end" 
                      onClick={() => onRemove(item.id)}
                      sx={{ color: '#ff5252' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.title}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {item.unitCode && (
                          <Chip 
                            label={item.unitCode} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem',
                              bgcolor: 'rgba(0, 150, 136, 0.2)' 
                            }} 
                          />
                        )}
                        {item.year && (
                          <Chip 
                            label={item.year} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem',
                              bgcolor: 'rgba(0, 150, 136, 0.2)' 
                            }} 
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < items.length - 1 && <Divider sx={{ my: 1, opacity: 0.1 }} />}
              </React.Fragment>
            ))}
          </List>

          <Tooltip 
            title={`Will be saved as: ${fileName.trim() || 'merged_exam_papers'}.pdf`}
            placement="top"
            arrow
          >
            <TextField
              fullWidth
              size="small"
              label="File Name"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="merged_exam_papers"
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 150, 136, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 150, 136, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#009688',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#009688',
                },
              }}
              helperText="PDF will be saved as [name].pdf"
            />
          </Tooltip>

          <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0, 150, 136, 0.1)', borderRadius: 1 }}>
            <FormControlLabel
              control={
                <Switch 
                  size="small"
                  checked={mergeOptions.ocr}
                  onChange={(e) => setMergeOptions({...mergeOptions, ocr: e.target.checked})}
                  sx={{ 
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#009688' }
                  }}
                />
              }
              label={<Typography variant="caption">Enable OCR</Typography>}
            />
            <FormControlLabel
              control={
                <Switch 
                  size="small"
                  checked={mergeOptions.bookmarks}
                  onChange={(e) => setMergeOptions({...mergeOptions, bookmarks: e.target.checked})}
                  sx={{ 
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#009688' }
                  }}
                />
              }
              label={<Typography variant="caption">Add Bookmarks</Typography>}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="Clear all documents">
              <IconButton 
                onClick={handleClearAll}
                sx={{ 
                  color: '#ff5252',
                  border: '1px solid rgba(255, 82, 82, 0.3)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 82, 82, 0.1)',
                  }
                }}
              >
                <ClearAllIcon />
              </IconButton>
            </Tooltip>
            
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={merging ? <CircularProgress size={20} /> : <MergeIcon />}
              onClick={handleMerge}
              disabled={merging}
              sx={{
                bgcolor: '#009688',
                '&:hover': {
                  bgcolor: '#00796b',
                },
                py: 1.5,
              }}
            >
              {merging ? 'Merging...' : 'Merge & Download'}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );

  return (
    <>
      {/* Desktop - Always visible */}
      <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
        {basketContent}
      </Box>

      {/* Mobile - Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={onMobileClose}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: '90%',
            maxWidth: 400,
            bgcolor: 'transparent',
            p: 2,
          },
        }}
      >
        {basketContent}
      </Drawer>
    </>
  );
}

export default Basket;
